import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid } from "recharts";

export default function VendorAnalytics() {
  const { vendor } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/vendor/orders").then(r => r.data),
      api.get("/vendor/products").then(r => r.data),
    ]).then(([o, p]) => { setOrders(o); setProducts(p); });
  }, []);

  const revenueByDay = useMemo(() => {
    const map = {};
    orders.filter(o => !["pending_payment", "cancelled"].includes(o.status)).forEach(o => {
      const d = new Date(o.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      const mine = o.items.filter(it => it.vendor_id === vendor.id).reduce((a, it) => a + it.price * it.quantity, 0);
      map[d] = (map[d] || 0) + mine;
    });
    return Object.entries(map).map(([date, revenue]) => ({ date, revenue }));
  }, [orders, vendor.id]);

  const topProducts = useMemo(() => {
    const map = {};
    orders.forEach(o => o.items.filter(it => it.vendor_id === vendor.id).forEach(it => {
      map[it.title] = (map[it.title] || 0) + it.quantity;
    }));
    return Object.entries(map).map(([name, units]) => ({ name, units })).sort((a, b) => b.units - a.units).slice(0, 5);
  }, [orders, vendor.id]);

  const stats = useMemo(() => {
    const paid = orders.filter(o => !["pending_payment", "cancelled"].includes(o.status));
    const returns = orders.filter(o => o.status.includes("return") || o.status === "refunded");
    const revenue = paid.reduce((s, o) => s + o.items.filter(it => it.vendor_id === vendor.id).reduce((a, it) => a + it.price * it.quantity, 0), 0);
    return {
      revenue, orders: paid.length, returns: returns.length,
      pending_approval: products.filter(p => !p.approved && !p.spotted_certificate).length,
      failure_rate: (vendor.failure_rate * 100).toFixed(1),
    };
  }, [orders, products, vendor]);

  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Numbers</p>
      <h1 className="font-serif text-6xl mt-2 mb-10 tracking-tighter">Analytics.</h1>

      <div className="grid md:grid-cols-4 gap-4 mb-10">
        {[
          { l: "Gross Revenue", v: `₹ ${stats.revenue.toLocaleString("en-IN")}` },
          { l: "Paid Orders", v: stats.orders },
          { l: "Returns", v: stats.returns },
          { l: "Failure Rate", v: `${stats.failure_rate}%` },
        ].map(k => (
          <div key={k.l} className="border border-foreground p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{k.l}</p>
            <p className="font-serif text-3xl mt-2">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-foreground p-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Revenue by Day</p>
          <div className="h-64">
            {revenueByDay.length === 0 ? (
              <div className="h-full flex items-center justify-center text-muted-foreground font-serif text-lg">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueByDay}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "monospace" }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--foreground))", borderRadius: 0 }} />
                  <Line type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="border border-foreground p-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Top Products (units sold)</p>
          <div className="h-64">
            {topProducts.length === 0 ? (
              <div className="h-full flex items-center justify-center text-muted-foreground font-serif text-lg">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topProducts}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fontFamily: "monospace" }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--foreground))", borderRadius: 0 }} />
                  <Bar dataKey="units" fill="hsl(var(--foreground))" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
